import React, { useState } from "react";
import { api } from "@/lib/api";
import { ButtonLux } from "@/components/primitives";

/** Public booking / consultation form. type: consultation | experience | project */
export default function BookingForm({ type, title, subtitle }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.createBooking({ type, ...form });
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      setError(e?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="py-8 flex flex-col items-start gap-4" data-testid="booking-success">
        <h3 className="font-serif text-3xl text-lux-ink font-light">Thank you.</h3>
        <p className="text-[15px] text-lux-sec leading-relaxed font-light max-w-md">
          We’ve received your request. A member of the Onedot6 studio will reach out within one working day.
        </p>
        <ButtonLux label="Submit Another" variant="ghost" onClick={() => setSuccess(false)} testId="booking-another" />
      </div>
    );
  }

  return (
    <div className="py-2" data-testid="booking-form">
      <p className="text-[11px] tracking-[0.28em] uppercase text-lux-accent font-medium mb-3">Inquiry</p>
      <h3 className="font-serif text-3xl leading-tight text-lux-ink font-light mb-2">{title}</h3>
      {subtitle ? <p className="text-[15px] text-lux-sec leading-relaxed font-light mb-7">{subtitle}</p> : <div className="h-2" />}

      <Field label="Name" value={form.name} onChange={set("name")} testId="form-name" />
      <Field label="Email" type="email" value={form.email} onChange={set("email")} testId="form-email" />
      <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} testId="form-phone" />
      <Field label="Tell us a little about your project (optional)" value={form.message} onChange={set("message")} testId="form-message" multiline />

      {error ? <p className="text-[#A33A2A] text-[13px] mt-2" data-testid="form-error">{error}</p> : null}

      <div className="mt-4">
        <ButtonLux label={loading ? "Sending…" : "Send Request"} onClick={submit} disabled={loading} testId="form-submit" />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", multiline = false }) {
  const cls =
    "w-full border-b border-lux-lineStrong bg-transparent py-2.5 text-[16px] text-lux-ink font-light outline-none focus:border-lux-ink transition-colors placeholder:text-lux-muted";
  return (
    <div className="mb-4.5 mb-5">
      <label className="block text-[11px] tracking-[0.15em] uppercase text-lux-muted mb-2">{label}</label>
      {multiline ? (
        <textarea data-testid={testId} value={value} onChange={onChange} rows={4} className={`${cls} resize-y min-h-[80px]`} />
      ) : (
        <input data-testid={testId} type={type} value={value} onChange={onChange} className={cls} />
      )}
    </div>
  );
}
